import { LitElement, html, css, nothing } from 'lit';
import { contactPrompt, contactLinkStyles } from './contact-prompt.js';
import {
  COMPED_TOTAL_COPY,
  couponStatusStyles,
  isCompedQuote,
  renderCouponStatusContent,
} from './ticket-coupon.js';
import { tieredTicketTotalLabel, type TicketTotalLine } from './ticket-total-label.js';
import { customElement, property } from 'lit/decorators.js';
import type { PublicEvent, PublicEventTicketTier } from '@yebo/shared';
import { isTicketTierSoldOut, tierPriceFloorCents } from '@yebo/shared';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';

/**
 * "Who's coming?" — party size selector.
 *
 * Two explicit counters: "Adults" (includes the booker, min 1) and "Kids".
 * Total party size = adults + kids — nobody is counted implicitly (#558: the
 * old "Additional people" counter made guests exclude themselves, and pax
 * arrived wrong at the venue). State keeps its shape (partySize = total,
 * kidsCount = subset), so deposit/ticket/availability math is untouched.
 */
@customElement('yebo-step-service')
export class YeboStepService extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    contactLinkStyles, // #592 / Round-16 A1 — legible on any venue palette
    couponStatusStyles, // #954 a11y M1 — the coupon live region lives here
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .step-content {
        flex: 1;
      }

      /* Counter row — matches Amelia pill style */
      .counter-row {
        display: flex;
        align-items: center;
        gap: var(--yebo-space-md);
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 12px 16px;
      }

      .counter-icon {
        width: 22px;
        height: 22px;
        color: var(--yebo-text-muted);
        flex-shrink: 0;
      }

      .counter-label {
        flex: 1;
        font-size: var(--yebo-font-size-base);
        color: var(--yebo-text);
        font-weight: 500;
      }

      .counter-controls {
        display: flex;
        align-items: center;
        gap: 2px;
        background: var(--yebo-bg-secondary);
        border-radius: var(--yebo-radius-sm);
        border: 1px solid var(--yebo-border);
      }

      .counter-btn {
        width: 34px;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: var(--yebo-text);
        font-size: 17.6px;
        cursor: pointer;
        transition: color var(--yebo-transition);
      }
      .counter-btn:hover:not(:disabled) {
        color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .counter-btn:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      @media (pointer: coarse) {
        .counter-btn {
          min-width: 44px;
          min-height: 44px;
        }
      }

      .counter-value {
        min-width: 28px;
        text-align: center;
        font-size: var(--yebo-font-size-base);
        font-weight: 600;
        color: var(--yebo-text);
      }

      .helper-text {
        margin-top: var(--yebo-space-sm);
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }

      /* MVP-4 — two-path chooser */
      .mode-chooser {
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-md);
      }
      .mode-card {
        display: flex;
        align-items: center;
        gap: var(--yebo-space-md);
        text-align: left;
        width: 100%;
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 18px 20px;
        cursor: pointer;
        transition: border-color var(--yebo-transition), background var(--yebo-transition);
      }
      .mode-card:hover {
        border-color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .mode-card-icon {
        width: 28px;
        height: 28px;
        color: var(--yebo-primary);
        flex-shrink: 0;
      }
      .mode-card-body {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
      }
      .mode-card-title {
        font-size: var(--yebo-font-size-lg);
        font-weight: 600;
        color: var(--yebo-text);
      }
      .mode-card-subtitle {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      @media (pointer: coarse) {
        .mode-card {
          min-height: 64px;
        }
      }

      /* MVP-4 — back-to-chooser affordance */
      .back-link {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: none;
        border: none;
        padding: 4px 0;
        margin-bottom: var(--yebo-space-md);
        color: var(--yebo-text-muted);
        font-size: var(--yebo-font-size-sm);
        cursor: pointer;
        transition: color var(--yebo-transition);
      }
      .back-link:hover {
        color: var(--yebo-primary);
      }

      /* #159 — special events */
      .events-section {
        margin-bottom: var(--yebo-space-lg);
      }
      .events-heading {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-text);
        margin-bottom: var(--yebo-space-sm);
      }
      .event-list {
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-sm);
      }
      .event-card {
        display: flex;
        flex-direction: column;
        gap: 2px;
        text-align: left;
        width: 100%;
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 12px 16px;
        cursor: pointer;
        transition: border-color var(--yebo-transition), background var(--yebo-transition);
      }
      .event-card:hover {
        border-color: var(--yebo-primary);
      }
      .event-card[aria-pressed='true'] {
        border-color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .event-card-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--yebo-space-md);
      }
      .event-name {
        font-size: var(--yebo-font-size-base);
        font-weight: 600;
        color: var(--yebo-event-heading);
      }
      .event-date {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        white-space: nowrap;
      }
      /* The card's one line of text: the event's DESCRIPTION, and only that
         (#899 — the menu note never appears on the card; it renders in full
         in .event-menu once the guest selects the event). A description is
         authored AS a summary and capped at 200 chars, so it is shown whole —
         no clamp. pre-line keeps any line break the venue typed. (The 2-line
         clamp #651 put here existed for a 500-char menu on the card, and was
         dead once the card stopped carrying it — G3 N1.) */
      .event-note {
        display: block;
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-event-description);
        white-space: pre-line;
      }

      /* The selected event's menu, in full, on the ticket step. */
      /* #650 — the event blurb, above the menu. No heading and no boxed
         surround: it reads as the event's own opening sentence, and giving it
         the same card treatment as "On the menu" would make two competing
         panels where the guest needs one line of context and then the courses. */
      .event-description {
        margin: var(--yebo-space-md) 0 0;
      }
      /* #651 — the menu/info PDF download. A button, not a link: the URL is
         minted on click (it expires in ~2 minutes) so there is nothing to put
         in an href at render time. */
      .event-pdf {
        display: inline-flex;
        align-items: center;
        gap: 0.4em;
        margin-top: 0.6em;
        padding: 0.45em 0.85em;
        border: 1px solid var(--yebo-border, #d4d4d8);
        border-radius: var(--yebo-radius, 6px);
        background: transparent;
        color: inherit;
        font: inherit;
        font-size: 14px;
        cursor: pointer;
      }
      .event-pdf:hover:not(:disabled) {
        border-color: var(--yebo-primary, #18181b);
      }
      .event-pdf:disabled {
        opacity: 0.6;
        cursor: default;
      }
      .event-pdf-error {
        margin-top: 0.4em;
        font-size: 13px;
        color: var(--yebo-error, #b91c1c);
      }

      .event-description-body {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text);
        white-space: pre-line;
      }
      .event-menu {
        margin: var(--yebo-space-md) 0 0;
        padding: 12px 14px;
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
      }
      .event-menu-heading {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-text);
        margin-bottom: 4px;
      }
      .event-menu-body {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        white-space: pre-line;
      }
      .event-deposit {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-primary);
        font-weight: 500;
      }

      /* Prepaid ticket pricing + availability */
      .event-price {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-event-price);
        font-weight: 600;
      }
      .event-card[aria-disabled='true'] {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .event-card[aria-disabled='true']:hover {
        border-color: var(--yebo-border);
      }
      .event-badge {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        white-space: nowrap;
      }
      .event-badge.soldout {
        color: var(--yebo-error);
      }
      .event-badge.remaining {
        color: var(--yebo-text-muted);
      }
      /* #130 — recurring "standard experience" series badge */
      .event-badge.recurring {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--yebo-primary);
      }
      .event-badge.recurring svg {
        width: 13px;
        height: 13px;
      }

      /* Tier selector */
      /* #652 — per-tier quantity rows. A basket, not a single choice: the
         guest can take 2 VIP and 1 Standard in one booking, so each type needs
         its own counter rather than one radio across all of them. */
      .tier-qty-row {
        display: flex;
        align-items: center;
        gap: 0.75em;
        padding: 0.6em 0;
        border-bottom: 1px solid var(--yebo-border, #e4e4e7);
      }
      .tier-qty-row:last-of-type {
        border-bottom: none;
      }
      .tier-qty-main {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.15em;
      }
      .tier-qty-price {
        font-size: 13px;
        color: var(--yebo-muted, #71717a);
      }
      .tier-kids-row {
        display: flex;
        align-items: center;
        gap: 0.75em;
        padding: 0.4em 0 0.6em 1.25em;
        border-bottom: 1px solid var(--yebo-border, #e4e4e7);
      }
      .tier-kids-label {
        flex: 1 1 auto;
        font-size: 14px;
        color: var(--yebo-muted, #71717a);
      }
      .basket-summary {
        margin-top: 0.5em;
        font-size: 13px;
        color: var(--yebo-muted, #71717a);
      }

      .tier-section {
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-sm);
        margin: var(--yebo-space-md) 0 var(--yebo-space-lg);
      }
      .tier-heading {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-text);
      }
      .tier-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--yebo-space-md);
        text-align: left;
        width: 100%;
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 10px 14px;
        cursor: pointer;
        transition: border-color var(--yebo-transition), background var(--yebo-transition);
      }
      .tier-option[disabled] {
        cursor: not-allowed;
        opacity: 0.55;
      }
      .tier-option[disabled] .tier-price {
        text-decoration: line-through;
      }
      .tier-soldout {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-error);
        white-space: nowrap;
      }
      .tier-option:not([disabled]):hover {
        border-color: var(--yebo-primary);
      }
      .tier-option[aria-pressed='true'] {
        border-color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .tier-main {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .tier-label {
        font-size: var(--yebo-font-size-base);
        font-weight: 500;
        color: var(--yebo-event-heading);
      }
      .tier-desc {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-event-description);
      }
      .tier-price {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-event-price);
        white-space: nowrap;
      }

      /* Live ticket total */
      .ticket-total {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--yebo-space-md);
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 12px 16px;
        margin-bottom: var(--yebo-space-md);
      }
      .ticket-total-label {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      .ticket-total-value {
        font-size: var(--yebo-font-size-lg);
        font-weight: 700;
        color: var(--yebo-text-heading);
      }
      .ticket-error {
        color: var(--yebo-error);
        font-size: var(--yebo-font-size-sm);
        margin-bottom: var(--yebo-space-md);
      }

      /* Bottom action bar */
      .actions {
        display: flex;
        justify-content: flex-end;
        padding-top: var(--yebo-space-lg);
      }
    `,
  ];

  @property({ type: Number, attribute: 'party-size' })
  partySize = 2;

  @property({ type: Number, attribute: 'kids-count' })
  kidsCount = 0;

  @property({ type: Number, attribute: 'max-party' })
  maxParty = 20;

  /** #592 — guest-facing contact details for the at-cap prompt. */
  @property({ type: String, attribute: 'contact-phone' })
  contactPhone = '';

  @property({ type: String, attribute: 'contact-email' })
  contactEmail = '';

  /** #159 — active special events the guest can book. */
  @property({ attribute: false })
  events: PublicEvent[] = [];

  @property({ type: String, attribute: 'selected-event-id' })
  selectedEventId = '';

  /**
   * MVP-4 — the guest's chosen entry path: '' (chooser), 'table', or 'events'.
   * Empty string mirrors the store's `null` (no path chosen yet). The two-card
   * chooser renders only when events exist AND this is empty.
   */
  @property({ type: String, attribute: 'booking-mode' })
  bookingMode = '';

  // ── Prepaid event ticketing ────────────────────────────────────────────────

  /** Price tier the guest picked (for ticketed events with tiers). */
  @property({ type: String, attribute: 'selected-tier-id' })
  selectedTierId = '';

  /** Authoritative live quote total (cents) for the ticketed selection. */
  @property({ type: Number, attribute: 'ticket-total-cents' })
  ticketTotalCents = 0;

  @property({ type: Number, attribute: 'ticket-unit-cents' })
  ticketUnitCents = 0;

  /** Per-kid cover price when it differs from the adult rate; 0 = same as adults. */
  @property({ type: Number, attribute: 'ticket-kids-unit-cents' })
  ticketKidsUnitCents = 0;

  @property({ type: String, attribute: 'ticket-currency' })
  ticketCurrency = '';

  /** Covers still available for the selected ticketed event (−1 ⇒ unknown). */
  @property({ type: Number, attribute: 'ticket-remaining' })
  ticketRemaining = -1;

  @property({ type: Boolean, attribute: 'ticket-sold-out' })
  ticketSoldOut = false;

  /** User-facing reason a quote was rejected (e.g. party-size / sold out). */
  @property({ type: String, attribute: 'ticket-error' })
  ticketError = '';

  // ── #816 — the promo code ──────────────────────────────────────────────────
  // Everything here comes DOWN from the parent (which owns the quote) and goes
  // UP as `coupon-apply` / `coupon-clear`. This component renders the server's
  // numbers; it computes no money and holds no coupon state of its own.

  /** Server-computed pre-discount ticket total (cents); 0 = no coupon. */
  @property({ type: Number, attribute: 'ticket-subtotal-cents' })
  ticketSubtotalCents = 0;

  /** Server-computed discount (cents); 0 = no coupon. */
  @property({ type: Number, attribute: 'ticket-discount-cents' })
  ticketDiscountCents = 0;

  /** What is in the promo field (normalised). */
  @property({ type: String, attribute: 'coupon-code' })
  couponCode = '';

  /** The code the server ACCEPTED ('' when none). */
  @property({ type: String, attribute: 'coupon-applied-code' })
  couponAppliedCode = '';

  /** A label only — never an input to arithmetic. */
  @property({ type: Number, attribute: 'coupon-percent-off' })
  couponPercentOff = 0;

  /** −1 ⇒ the server sent no number ⇒ UNKNOWN (no pool hint, never "unlimited"). */
  @property({ type: Number, attribute: 'coupon-tickets-remaining' })
  couponTicketsRemaining = -1;

  /** The SERVER's refusal sentence. */
  @property({ type: String, attribute: 'coupon-error' })
  couponError = '';

  @property({ type: Boolean, attribute: 'coupon-applying' })
  couponApplying = false;

  /** #954 — the server's label for an accepted TYPED code ("Buy 1 Get 1 Free"). */
  @property({ type: String, attribute: 'coupon-applied-label' })
  couponAppliedLabel = '';

  // #954 — the event's AUTO deal, exactly as the last quote described it. All
  // of it is passed straight to <yebo-ticket-coupon>; nothing here derives it.
  @property({ type: Boolean, attribute: 'deal-applied' })
  dealApplied = false;

  @property({ type: String, attribute: 'deal-label' })
  dealLabel = '';

  @property({ type: String, attribute: 'deal-reason' })
  dealReason = '';

  @property({ type: String, attribute: 'deal-message' })
  dealMessage = '';

  // #651 — the menu/info PDF button. The async work lives in the parent (which
  // owns apiClient), so busy/error come DOWN as properties exactly like
  // `ticket-error` above, and the click goes UP as `menu-pdf-request`.
  // #652 — the basket. Comes DOWN as a property (the parent owns state) and
  // changes go UP as `ticket-lines-change`; party size and kids ride on that
  // event rather than being tracked here, so they cannot drift from the lines.
  @property({ attribute: false })
  ticketLines: Array<{ tierId: string; qty: number; kidsQty: number }> = [];

  /** r93 X1 — the SERVER's quote lines (`ticketQuote.lines`) for a basket
   *  quote. Read for the total row's label only — counts and tier names. */
  @property({ attribute: false })
  ticketQuoteLines: TicketTotalLine[] = [];

  @property({ type: Boolean, attribute: 'menu-pdf-busy' })
  menuPdfBusy = false;

  @property({ type: String, attribute: 'menu-pdf-error' })
  menuPdfError = '';

  private get _selectedEvent(): PublicEvent | null {
    return this.events.find((e) => e.id === this.selectedEventId) ?? null;
  }

  /** #130 — true when the selected card is a recurring series (no fixed date). */
  private get _isRecurring(): boolean {
    return !!this._selectedEvent?.recurring;
  }

  /** The ticketing config of the selected event, if it is a ticketed event. */
  private get _ticketing(): NonNullable<PublicEvent['ticketing']> | null {
    return this._selectedEvent?.ticketing ?? null;
  }

  /** True when an event card should be non-selectable (its tickets are gone). */
  private _eventDisabled(ev: PublicEvent): boolean {
    // #130 — a recurring series card is NEVER disabled from /config availability.
    // The card carries no per-occurrence covers (the series spans many dates, and
    // /config does zero per-occurrence reads). Even if its NEXT occurrence is sold
    // out, OTHER dates may be open — the guest discovers that in the date step via
    // the lazy occurrences endpoint. Disabling here would wrongly hide an open
    // series. Per-date sold-out is enforced later (occurrence picker + quote).
    if (ev.recurring) return false;
    const t = ev.ticketing;
    if (!t) return false;
    if (t.soldOut) return true;
    // If we know the remaining covers, the booker's party must fit.
    if (typeof t.coversRemaining === 'number' && t.coversRemaining < this.partySize) return true;
    return false;
  }

  // ── MVP-4: two-path entry ──────────────────────────────────────────────────

  /** True when the two-path chooser should show (events exist, no path chosen). */
  private get _showChooser(): boolean {
    return this.events.length > 0 && !this.bookingMode;
  }

  /** True when the guest is on the events path (only reachable when events exist). */
  private get _eventsMode(): boolean {
    return this.bookingMode === 'events';
  }

  /**
   * True when the regular party-size booking UI should show. This is the events
   * === 0 case (unchanged behavior) and the explicitly-chosen 'table' path.
   */
  private get _tableMode(): boolean {
    return !this._showChooser && !this._eventsMode;
  }

  /** Guest tapped a chooser card — surface the chosen path to the host. */
  private _selectMode(mode: 'table' | 'events') {
    this.dispatchEvent(new CustomEvent('booking-mode-select', {
      detail: { mode },
      bubbles: true,
      composed: true,
    }));
  }

  /** Guest tapped "‹ Back" — return to the two-path chooser. */
  private _backToChooser() {
    this.dispatchEvent(new CustomEvent('booking-mode-back', {
      bubbles: true,
      composed: true,
    }));
  }

  private _selectEvent(ev: PublicEvent) {
    if (this._eventDisabled(ev)) return;
    // Toggle: clicking the selected event clears it.
    const next = this.selectedEventId === ev.id ? null : ev;
    this.dispatchEvent(new CustomEvent('event-select', {
      detail: next ? { eventId: next.id, name: next.name, date: next.date } : { eventId: null },
      bubbles: true,
      composed: true,
    }));
  }

  /** #601 — a HARD-capped tier with nothing left for this party. The predicate
   *  is shared with the server so the two can never disagree. */
  private _tierSoldOut(tier: PublicEventTicketTier): boolean {
    return isTicketTierSoldOut(tier, this.partySize || 1);
  }

  private _selectTier(tierId: string) {
    // #601 — belt and braces: the button is disabled, but a stray programmatic
    // click must not put the guest into a checkout the server will refuse.
    const tier = (this._ticketing?.tiers ?? []).find((t) => t.id === tierId);
    if (tier && this._tierSoldOut(tier)) return;
    // Toggle off only stays selected when tiers are required, so just set it.
    this.dispatchEvent(new CustomEvent('tier-select', {
      detail: { tierId },
      bubbles: true,
      composed: true,
    }));
  }

  private _formatPrice(cents: number, currency: string): string {
    const major = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase(),
      }).format(major);
    } catch {
      return `${(currency || 'USD').toUpperCase()} ${major.toFixed(2)}`;
    }
  }

  /**
   * #601 — the cheapest price a guest can ACTUALLY buy.
   *
   * Quoting a sold-out tier's price is a promise the checkout then breaks, so
   * the floor is taken over available tiers. When every tier is gone the full
   * range is used again: with nothing buyable, the lowest price is a more
   * honest floor than no price at all (the card already carries its own
   * sold-out badge to say so).
   */
  private _tierPriceFloor(t: NonNullable<PublicEvent['ticketing']>): number | null {
    return tierPriceFloorCents(t.tiers, this.partySize || 1);
  }

  /** Per-person price shown on a ticketed event card (base, before tier pick). */
  private _eventCardPrice(t: NonNullable<PublicEvent['ticketing']>): string {
    const floor = this._tierPriceFloor(t);
    const cents = floor ?? t.pricePerPersonCents;
    const prefix = floor != null ? 'From ' : '';
    return `${prefix}${this._formatPrice(cents, t.currency)} pp`;
  }

  /**
   * #130 — the "from $X pp · next <date>" summary shown on a recurring series
   * card. Price comes from `ticketing.pricePerPersonCents` (the same field a
   * one-off uses); the next bookable date is `nextOccurrence` (a 'YYYY-MM-DD'
   * string) falling back to `date`. Both are server-projected on the card; the
   * widget does no per-occurrence reads here.
   */
  private _recurringCardSummary(ev: PublicEvent): string {
    const t = ev.ticketing;
    const parts: string[] = [];
    if (t) {
      const cents = this._tierPriceFloor(t) ?? t.pricePerPersonCents;
      parts.push(`From ${this._formatPrice(cents, t.currency)} pp`);
    } else if (ev.depositPerPerson) {
      parts.push(`Deposit ${ev.depositPerPerson} pp`);
    }
    const next = ev.nextOccurrence || ev.date;
    if (next) parts.push(`next ${this._formatEventDate(next)}`);
    return parts.join(' · ');
  }

  private _formatEventDate(date: string): string {
    const d = new Date(`${date}T12:00:00`);
    return Number.isNaN(d.getTime())
      ? date
      : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  }

  /** Adults = partySize − kidsCount (the booker is an adult; min 1). */
  private get _adults(): number {
    return Math.max(1, this.partySize - this.kidsCount);
  }

  /** Lower party-size bound — a ticketed event may set a minimum. */
  private get _minParty(): number {
    return Math.max(1, this._ticketing?.minPartySize ?? 1);
  }

  /**
   * Upper party-size bound — the tighter of the widget max, any per-event
   * ticket ceiling, and the covers still remaining for a ticketed event.
   */
  private get _maxParty(): number {
    const t = this._ticketing;
    let cap = this.maxParty;
    if (t?.maxPartySize) cap = Math.min(cap, t.maxPartySize);
    if (t && typeof t.coversRemaining === 'number') cap = Math.min(cap, t.coversRemaining);
    return Math.max(this._minParty, cap);
  }

  private _updateAdults(delta: number) {
    // Adults move the TOTAL while kids stay fixed. Floor: at least 1 adult
    // (kids + 1) and any ticketed-event minimum; ceiling: the widget max.
    const lower = Math.max(this._minParty, this.kidsCount + 1);
    const next = Math.max(lower, Math.min(this._maxParty, this.partySize + delta));
    this.dispatchEvent(new CustomEvent('party-size-change', {
      detail: { partySize: next },
      bubbles: true,
      composed: true,
    }));
  }

  private _updateKids(delta: number) {
    // Kids also move the TOTAL (total = adults + kids); adults stay fixed.
    // A ticketed-event minimum holds the total up, the widget max caps it.
    const adults = this._adults;
    const minKids = Math.max(0, this._minParty - adults);
    const maxKids = Math.max(minKids, this._maxParty - adults);
    const next = Math.max(minKids, Math.min(maxKids, this.kidsCount + delta));
    this.dispatchEvent(new CustomEvent('kids-count-change', {
      detail: { kidsCount: next },
      bubbles: true,
      composed: true,
    }));
    const total = adults + next;
    if (total !== this.partySize) {
      this.dispatchEvent(new CustomEvent('party-size-change', {
        detail: { partySize: total },
        bubbles: true,
        composed: true,
      }));
    }
  }

  private _continue() {
    this.dispatchEvent(new CustomEvent('step-continue', {
      bubbles: true,
      composed: true,
    }));
  }

  /** "R300 pp × 4 guests" or, with kids priced separately, an adult+kid breakdown.
   *  r93 X1 — a TIERED basket is priced by lines and has no single unit price,
   *  so it reads "2 × VIP + 1 × General" from the server's lines instead. */
  private _ticketTotalLabel(fallbackCurrency: string) {
    const tiered = tieredTicketTotalLabel(
      this.ticketQuoteLines,
      (tierId) => (this._ticketing?.tiers ?? []).find((t) => t.id === tierId)?.label,
    );
    if (tiered) return tiered;
    const currency = this.ticketCurrency || fallbackCurrency;
    const unitFormatted = this._formatPrice(this.ticketUnitCents, currency);
    if (this.kidsCount > 0) {
      const adults = this.partySize - this.kidsCount;
      const kidsFormatted = this._formatPrice(this.ticketKidsUnitCents, currency);
      return html`${unitFormatted} × ${adults} adult${adults !== 1 ? 's' : ''} + ${kidsFormatted} × ${this.kidsCount} kid${this.kidsCount !== 1 ? 's' : ''}`;
    }
    return html`${unitFormatted} pp × ${this.partySize} guest${this.partySize !== 1 ? 's' : ''}`;
  }

  /**
   * #651 / #899 — the one line of text a card carries: the event's
   * `description`, and nothing else.
   *
   * #899 defined the two fields' roles: `description` is the event's own
   * description; `menuNote` is an optional highlight of what is on the menu.
   * They are not interchangeable, so the menu note is NEVER shown here — not
   * even as a fallback for an undescribed event (#651 kept that fallback; #899
   * reverses it). The menu renders in full, labelled "On the menu", once the
   * guest selects the event (`_renderEventMenu`).
   *
   * Accepted cost: an event with a menu note and no description shows no
   * summary line on its card until the venue writes a description.
   */
  private _cardSummary(ev: PublicEvent) {
    const desc = ev.description?.trim();
    if (!desc) return '';
    return html`<span class="event-note">${desc}</span>`;
  }

  /**
   * #650 — the event's short blurb, rendered ABOVE the menu.
   *
   * Requested by Maryke (Perdeberg): the description must come before the menu
   * items. Until `description` existed there was nothing to order — venues
   * pasted the blurb and the courses into one `menuNote` box and the widget
   * printed the blob under "On the menu".
   *
   * Absent on every existing event, so a venue that has not filled it in
   * renders exactly as before: this returns `nothing` and the menu section is
   * still the first thing under the title.
   */
  private _renderEventDescription() {
    const desc = this._selectedEvent?.description?.trim();
    if (!desc) return nothing;
    return html`
      <section class="event-description" aria-label="About this event">
        <div class="event-description-body">${desc}</div>
      </section>
    `;
  }

  /**
   * #651 — the downloadable menu / info PDF.
   *
   * `hasMenuPdf` is the only thing the public config carries; the S3 key never
   * leaves the server. A <button>, not an <a>: the presigned URL is minted on
   * click and expires in ~2 minutes, so there is no href to render up front.
   */
  private _renderEventPdf() {
    if (!this._selectedEvent?.hasMenuPdf) return nothing;
    return html`
      <div>
        <button
          type="button"
          class="event-pdf"
          ?disabled=${this.menuPdfBusy}
          @click=${this._onDownloadPdf}
        >
          ${this.menuPdfBusy ? 'Opening…' : 'Download the menu (PDF)'}
        </button>
        ${this.menuPdfError
          ? html`<div class="event-pdf-error" role="alert">${this.menuPdfError}</div>`
          : nothing}
      </div>
    `;
  }

  private _onDownloadPdf() {
    const eventId = this._selectedEvent?.id;
    if (!eventId || this.menuPdfBusy) return;
    this.dispatchEvent(new CustomEvent('menu-pdf-request', {
      detail: { eventId },
      bubbles: true,
      composed: true,
    }));
  }

  /**
   * The selected event's menu note, in full, on the ticket step.
   *
   * The note used to live ONLY on the list card, so the moment a guest tapped
   * the event the food menu disappeared and the step showed nothing but tier
   * blurbs — the guest chose (and prepaid for) a ticket without being able to
   * see what they were eating. It renders above the tier selector because it is
   * the context for that choice, and for ticketed AND deposit events alike.
   */
  private _renderEventMenu() {
    const note = this._selectedEvent?.menuNote?.trim();
    if (!note) return nothing;
    return html`
      <section class="event-menu" aria-label="Event menu">
        <div class="event-menu-heading">On the menu</div>
        <div class="event-menu-body">${note}</div>
      </section>
    `;
  }

  /** #652 — total seats across the basket (children included). */
  private get _basketSeats(): number {
    return this.ticketLines.reduce((n, l) => n + l.qty, 0);
  }

  /** #652 — children across the basket. */
  private get _basketKids(): number {
    return this.ticketLines.reduce((n, l) => n + l.kidsQty, 0);
  }

  private _lineFor(tierId: string) {
    return this.ticketLines.find((l) => l.tierId === tierId);
  }

  /**
   * #652 — one quantity row per ticket type, plus a nested children counter.
   *
   * The children counter is per TIER, not per booking, because `qty` counts
   * seats: once a child occupies a specific tier's seat, one event-level
   * "kids" number no longer determines the bill (2xVIP + 1xStandard with one
   * child is two different totals depending on which seat the child takes).
   * A child still pays one flat rate whatever tier they sit on — this asks
   * which SEAT they take, not which price they pay. It only appears once that
   * tier has a seat to put a child in, and only when the event has a kids rate.
   */
  private _renderTierQty(tier: PublicEventTicketTier, t: NonNullable<typeof this._ticketing>) {
    const line = this._lineFor(tier.id);
    const qty = line?.qty ?? 0;
    const kidsQty = line?.kidsQty ?? 0;
    const gone = this._tierSoldOut(tier);
    // G3 payments M5 — `this.maxParty` is the RAW location cap. `_maxParty`
    // folds in the event's own `maxPartySize` AND `coversRemaining`, and it is
    // what the legacy adult/kid steppers already use. Capping on the raw number
    // let a guest build 20 seats for a `maxPartySize: 4` event (then /quote
    // refuses with `party_above_max` and the total silently blanks) or for an
    // event with 3 covers left (then /checkout 409s, which the widget renders
    // as "this event just sold out" — false, for an event with seats).
    const atMax = this._basketSeats >= this._maxParty;
    const hasKidsRate = typeof t.kidsPricePerPersonCents === 'number';

    return html`
      <div class="tier-qty-row">
        <span class="tier-qty-main">
          <span class="tier-label">${tier.label}</span>
          ${tier.description ? html`<span class="tier-desc">${tier.description}</span>` : nothing}
          <span class="tier-qty-price">
            ${gone
              ? 'Sold out'
              : `${this._formatPrice(tier.pricePerPersonCents, t.currency)} pp`}
          </span>
        </span>
        <span class="counter-controls">
          <button
            type="button"
            class="counter-btn"
            ?disabled=${qty <= 0}
            aria-label=${`One fewer ${tier.label} ticket`}
            @click=${() => this._setTierQty(tier.id, qty - 1)}
          >&minus;</button>
          <span class="counter-value" aria-live="polite">${qty}</span>
          <button
            type="button"
            class="counter-btn"
            ?disabled=${gone || atMax}
            aria-label=${`One more ${tier.label} ticket`}
            @click=${() => this._setTierQty(tier.id, qty + 1)}
          >+</button>
        </span>
      </div>
      ${qty > 0 && hasKidsRate ? html`
        <div class="tier-kids-row">
          <span class="tier-kids-label">
            of which children
            <span class="tier-qty-price">
              (${this._formatPrice(t.kidsPricePerPersonCents!, t.currency)} each)
            </span>
          </span>
          <span class="counter-controls">
            <button
              type="button"
              class="counter-btn"
              ?disabled=${kidsQty <= 0}
              aria-label=${`One fewer child on ${tier.label}`}
              @click=${() => this._setTierKids(tier.id, kidsQty - 1)}
            >&minus;</button>
            <span class="counter-value" aria-live="polite">${kidsQty}</span>
            <button
              type="button"
              class="counter-btn"
              ?disabled=${kidsQty >= qty}
              aria-label=${`One more child on ${tier.label}`}
              @click=${() => this._setTierKids(tier.id, kidsQty + 1)}
            >+</button>
          </span>
        </div>
      ` : nothing}
    `;
  }

  private _setTierQty(tierId: string, next: number) {
    const qty = Math.max(0, next);
    // #601 belt and braces, carried over from the single-select UI: the `+`
    // button is disabled on a sold-out tier, but a stray programmatic click
    // must not put the guest into a checkout the server will refuse. Only
    // INCREASES are blocked — a guest must always be able to remove a ticket
    // from a tier that sold out while they were deciding.
    const current = this._lineFor(tierId)?.qty ?? 0;
    if (qty > current) {
      const tier = (this._ticketing?.tiers ?? []).find((t) => t.id === tierId);
      if (tier && this._tierSoldOut(tier)) return;
    }
    const lines = this.ticketLines
      .map((l) => (l.tierId === tierId
        // Dropping seats must drop any children that no longer have one, or the
        // basket would price a negative number of adults.
        ? { ...l, qty, kidsQty: Math.min(l.kidsQty, qty) }
        : l))
      .filter((l) => l.qty > 0);
    if (qty > 0 && !lines.some((l) => l.tierId === tierId)) {
      lines.push({ tierId, qty, kidsQty: 0 });
    }
    this._emitLines(lines);
  }

  private _setTierKids(tierId: string, next: number) {
    const lines = this.ticketLines.map((l) => {
      if (l.tierId !== tierId) return l;
      return { ...l, kidsQty: Math.min(Math.max(0, next), l.qty) };
    });
    this._emitLines(lines);
  }

  /** The basket is authoritative: party size and kids are DERIVED from it, never
   *  tracked alongside it. Two sources for one quantity is how they drift. */
  private _emitLines(lines: Array<{ tierId: string; qty: number; kidsQty: number }>) {
    this.dispatchEvent(new CustomEvent('ticket-lines-change', {
      detail: {
        lines,
        partySize: lines.reduce((n, l) => n + l.qty, 0),
        kidsCount: lines.reduce((n, l) => n + l.kidsQty, 0),
      },
      bubbles: true,
      composed: true,
    }));
  }

  /** Tier selector + live total, shown for the selected ticketed event. */
  private _renderTicketing() {
    const t = this._ticketing;
    if (!t) return nothing;
    // #130 — a recurring series shows its tier SELECTOR here (so the guest picks
    // their menu before choosing a date), but the live TOTAL is gated on
    // `ticketTotalCents > 0`, which stays 0 until an occurrence is quoted on the
    // date step. So the selector renders now and the total appears later — no
    // special-casing needed beyond letting the shared block through.

    const tiers = t.tiers ?? [];
    return html`
      ${tiers.length > 0 ? html`
        <div class="tier-section">
          <div class="tier-heading">Choose your tickets</div>
          ${tiers.map((tier) => this._renderTierQty(tier, t))}
          ${this._basketSeats > 0 ? html`
            <div class="basket-summary">
              ${this._basketSeats} ${this._basketSeats === 1 ? 'ticket' : 'tickets'}${
                this._basketKids > 0
                  ? html` &middot; ${this._basketKids} ${this._basketKids === 1 ? 'child' : 'children'}`
                  : nothing
              }
            </div>
          ` : nothing}
        </div>
      ` : nothing}

      ${this.ticketError ? html`<p class="ticket-error">${this.ticketError}</p>` : nothing}

      ${this._showCouponBlock ? html`
        <yebo-ticket-coupon
          external-status
          code=${this.couponCode}
          applied-code=${this.couponAppliedCode}
          percent-off=${this.couponPercentOff}
          subtotal-cents=${this.ticketSubtotalCents}
          discount-cents=${this.ticketDiscountCents}
          currency=${this.ticketCurrency || t.currency}
          tickets-remaining=${this.couponTicketsRemaining}
          error=${this.couponError}
          ?applying=${this.couponApplying}
          applied-label=${this.couponAppliedLabel}
          ?deal-applied=${this.dealApplied}
          deal-label=${this.dealLabel}
          deal-reason=${this.dealReason}
          deal-message=${this.dealMessage}
        ></yebo-ticket-coupon>
      ` : nothing}

      <!--
        #954 a11y M1/M2 — the coupon's ONE live region. Rendered with the
        ticketing block, i.e. before the first quote, and OUTSIDE the coupon's
        own conditional, so the first deal line / nudge / refusal arrives as a
        mutation of an existing region (a region inserted already populated is
        not announced). The coupon is told "external-status" so it says none
        of this itself — one speaker.
      -->
      <div class="coupon-live" role="status" aria-live="polite">${this._showCouponBlock
        ? renderCouponStatusContent({
            appliedCode: this.couponAppliedCode,
            dealApplied: this.dealApplied,
            dealLabel: this.dealLabel,
            dealMessage: this.dealMessage,
            discountCents: this.ticketDiscountCents,
            currency: this.ticketCurrency || t.currency,
            error: this.couponError,
          })
        : nothing}</div>

      ${(this.ticketTotalCents > 0 || this._isComped) && !this.ticketSoldOut && !this._tieredBasketEmpty ? html`
        <div class="ticket-total">
          <span class="ticket-total-label">${this._ticketTotalLabel(t.currency)}</span>
          <span class="ticket-total-value">
            ${this._isComped
              ? COMPED_TOTAL_COPY
              : this._formatPrice(this.ticketTotalCents, this.ticketCurrency || t.currency)}
          </span>
        </div>
      ` : nothing}
    `;
  }

  /**
   * #953 — a 100%-comped quote: the SERVER accepted a code, the pre-discount
   * subtotal is a real price, and the server's total is 0. Nothing here is
   * computed — all three are the server's numbers. A zero total with NO applied
   * coupon is deliberately NOT comped (that is #823's separate decision).
   */
  private get _isComped(): boolean {
    return isCompedQuote(this.ticketTotalCents, this.ticketSubtotalCents, this.couponAppliedCode, this.dealApplied);
  }

  /**
   * #953 — the promo block shows whenever there is a real price to discount:
   * a full-price quote (`total > 0`; the server sends no subtotal without a
   * code) OR a discounted one (`subtotal > 0`), so at 100% off the applied chip
   * and its Remove button stay on screen instead of vanishing with the total.
   */
  private get _showCouponBlock(): boolean {
    if (this._tieredBasketEmpty) return false;
    return (this.ticketTotalCents > 0 || this.ticketSubtotalCents > 0) && !this.ticketSoldOut;
  }

  /**
   * r93 X2 — a tiered event with nothing in the basket has nothing to price.
   * The host no longer quotes it, and this keeps the view honest if a quote
   * from a just-emptied basket is still in state: no deal line, no total, and
   * nothing for the live region to announce.
   */
  private get _tieredBasketEmpty(): boolean {
    return (this._ticketing?.tiers ?? []).length > 0 && this._basketSeats <= 0;
  }

  /** Whether the Continue button is disabled (ticketed selection not buyable). */
  private get _continueDisabled(): boolean {
    const t = this._ticketing;
    if (!t) return false;
    // #130 — a recurring series is not priced on the service step (the guest
    // picks the occurrence — and gets a quote — on the NEXT (date) step). So a
    // missing total here must NOT block Continue; the occurrence picker gates it.
    // r93 X2 — except that a TIERED series still needs a ticket in the basket:
    // with none, the next step would have nothing to quote.
    if (this._isRecurring) return this._tieredBasketEmpty;
    if (this.ticketSoldOut) return true;
    if (!!this.ticketError) return true;
    // #652 — a tiered event now needs at least one ticket in the basket. The
    // total check below would catch an empty basket too, but only AFTER a
    // round-trip; this keeps Continue honest while the quote is in flight.
    if ((this._ticketing?.tiers ?? []).length > 0 && this._basketSeats <= 0) return true;
    // #953 — a zero total blocks UNLESS it is a server-accepted comp.
    if (this.ticketTotalCents <= 0 && !this._isComped) return true;
    return false;
  }

  /**
   * MVP-4 — the two-path entry chooser. Rendered on its own (no counters, no
   * event list) so the guest makes one clear choice: a regular table booking or
   * the special-events list. Matches the widget's card visual language.
   */
  private _renderChooser() {
    const count = this.events.length;
    return html`
      <div class="step-content">
        <h2 class="step-title">How would you like to book?</h2>
        <div class="mode-chooser">
          <button type="button" class="mode-card" @click=${() => this._selectMode('table')}>
            <svg class="mode-card-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <span class="mode-card-body">
              <span class="mode-card-title">Book a table</span>
              <span class="mode-card-subtitle">Reserve a table for your party</span>
            </span>
          </button>
          <button type="button" class="mode-card" @click=${() => this._selectMode('events')}>
            <svg class="mode-card-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20 12c0-1.1.9-2 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-1.99.9-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2zm-2.42 0c0 1.06.56 2.03 1.42 2.56V17H5v-2.44c.86-.53 1.42-1.5 1.42-2.56S5.86 9.97 5 9.44V7h14v2.44c-.86.53-1.42 1.5-1.42 2.56z"/>
            </svg>
            <span class="mode-card-body">
              <span class="mode-card-title">Events &amp; experiences</span>
              <span class="mode-card-subtitle">Tickets &amp; special occasions${count > 0 ? ` · ${count} event${count !== 1 ? 's' : ''}` : ''}</span>
            </span>
          </button>
        </div>
      </div>
    `;
  }

  /** #159 — the special-events list (events path only). */
  private _renderEvents() {
    return html`
      <div class="events-section">
        <div class="events-heading">Special events</div>
        <div class="event-list">
          ${this.events.map((ev) => {
            const t = ev.ticketing;
            const disabled = this._eventDisabled(ev);
            return html`
            <button
              type="button"
              class="event-card"
              aria-pressed=${this.selectedEventId === ev.id ? 'true' : 'false'}
              aria-disabled=${disabled ? 'true' : 'false'}
              ?disabled=${disabled}
              @click=${() => this._selectEvent(ev)}
            >
              <div class="event-card-top">
                <span class="event-name">${ev.name}</span>
                ${ev.recurring
                  ? html`<span class="event-badge recurring">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                      </svg>Repeats</span>`
                  : t && t.soldOut
                    ? html`<span class="event-badge soldout">Sold out</span>`
                    : t && typeof t.coversRemaining === 'number' && t.coversRemaining <= 10
                      ? html`<span class="event-badge remaining">${t.coversRemaining} left</span>`
                      : html`<span class="event-date">${this._formatEventDate(ev.date)}</span>`}
              </div>
              ${this._cardSummary(ev)}
              ${ev.recurring
                ? html`<span class="event-price">${this._recurringCardSummary(ev)}</span>`
                : t
                  ? html`<span class="event-price">${this._eventCardPrice(t)}</span>`
                  : ev.depositPerPerson
                    ? html`<span class="event-deposit">Deposit ${ev.depositPerPerson} pp</span>`
                    : ''}
            </button>
          `;
          })}
        </div>
      </div>
    `;
  }

  /** Party-size + kids counters (the regular booking UI). */
  private _renderCounters() {
    return html`
      <div class="counter-row">
        <svg class="counter-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
        <span class="counter-label">Adults</span>
        <div class="counter-controls">
          <button
            class="counter-btn"
            @click=${() => this._updateAdults(-1)}
            ?disabled=${this.partySize <= Math.max(this._minParty, this.kidsCount + 1)}
            aria-label="Decrease adults"
          >&minus;</button>
          <span class="counter-value" aria-live="polite">${this._adults}</span>
          <button
            class="counter-btn"
            @click=${() => this._updateAdults(1)}
            ?disabled=${this.partySize >= this._maxParty}
            aria-label="Increase adults"
          >+</button>
        </div>
      </div>
      <p class="helper-text">Number of adults, including you.</p>

      <div class="counter-row" style="margin-top: var(--yebo-space-md);">
        <svg class="counter-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
        </svg>
        <span class="counter-label">Kids</span>
        <div class="counter-controls">
          <button
            class="counter-btn"
            @click=${() => this._updateKids(-1)}
            ?disabled=${this.kidsCount <= Math.max(0, this._minParty - this._adults)}
            aria-label="Decrease kids"
          >&minus;</button>
          <span class="counter-value" aria-live="polite">${this.kidsCount}</span>
          <button
            class="counter-btn"
            @click=${() => this._updateKids(1)}
            ?disabled=${this.partySize >= this._maxParty}
            aria-label="Increase kids"
          >+</button>
        </div>
      </div>
      <p class="helper-text">Number of kids in your party (optional).</p>
      ${this.partySize >= this._maxParty
        ? html`<p class="helper-text" role="status" style="margin-top: var(--yebo-space-sm); font-weight: 600;">
            ${contactPrompt(
              'For larger parties, please contact the restaurant directly:',
              this.contactPhone,
              this.contactEmail,
            )}
          </p>`
        : nothing}
    `;
  }

  render() {
    // MVP-4 — the chooser precedes any booking form when events exist and no
    // path has been chosen yet.
    if (this._showChooser) {
      return this._renderChooser();
    }

    const ticketing = this._ticketing;
    const hasSelection = !!this._selectedEvent;
    // Events path: the party-size/ticketing rows only appear once an event is
    // selected (per the existing behavior). Table path: always shown.
    const showBookingForm = this._tableMode || hasSelection;
    // #652 / round-38 T1 (Kayla) — hide the Adults/Kids counters when the event
    // has per-tier steppers. They were rendering BELOW the ticket total, so the
    // guest saw two different ways to say how many people are coming, for the
    // same booking.
    //
    // It is not only clutter: with a basket, `partySize` and `kidsCount` are
    // DERIVED from the lines (Σ qty, Σ kidsQty). The old counters write those
    // same two fields directly, so moving one would have silently contradicted
    // the basket the guest had built — the exact two-sources-for-one-quantity
    // drift the server-side design went out of its way to avoid, reintroduced
    // through the UI. The tier rows already carry a children counter per line,
    // which is where the number has to be captured anyway.
    const tierSteppersShown = (this._ticketing?.tiers ?? []).length > 0;
    const showCounters = showBookingForm && !tierSteppersShown;
    // A "‹ Back" affordance returns to the chooser — only when a chooser exists
    // (i.e. there are events to choose between).
    const showBack = this.events.length > 0;

    return html`
      <div class="step-content">
        ${showBack ? html`
          <button type="button" class="back-link" @click=${this._backToChooser} aria-label="Back to booking options">
            &lsaquo; Back
          </button>
        ` : ''}
        <h2 class="step-title">${this._eventsMode ? 'Choose an event' : "Who's coming?"}</h2>

        ${this._eventsMode ? this._renderEvents() : ''}

        ${this._renderEventDescription()}

        ${this._renderEventPdf()}

        ${this._renderEventMenu()}

        ${this._renderTicketing()}

        ${showCounters ? this._renderCounters() : ''}
      </div>

      ${showBookingForm ? html`
        <div class="actions">
          <button
            class="btn btn-primary"
            @click=${this._continue}
            ?disabled=${this._continueDisabled}
          >
            ${ticketing && !this._isRecurring ? 'Continue to checkout' : 'Continue'}
          </button>
        </div>
      ` : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-step-service': YeboStepService;
  }
}
